import { disableBodyScroll, enableBodyScroll } from 'body-scroll-lock';

import delegateEvent from '../../../javascripts/events/delegateEvent';

const Dialog = () => ({
  dialogIsActiveClass: 'dialog--is-active',

  focusedElementBeforeOpen: document.activeElement,

  get focusableElements() {
    return Array.from(
      this.$root.querySelectorAll(
        'a[href], area[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), button:not([disabled]), [tabindex="0"]:not([hidden])'
      )
    );
  },

  get firstFocusableElement() {
    return this.focusableElements[0];
  },

  get lastFocusableElement() {
    return this.focusableElements.slice(-1)[0];
  },

  get isActive() {
    return this.$root.classList.contains(this.dialogIsActiveClass);
  },

  /**
   * When focus is on the first element, moves the focus to
   * the last element of a dialog
   * @param {any} event backward tab
   * @returns {void}
   */
  handleBackwardTab(event) {
    if (document.activeElement === this.firstFocusableElement) {
      event.preventDefault();
      this.lastFocusableElement.focus();
    }
  },

  /**
   * When focus is on the last element, moves the focus back to
   * the first element of a dialog
   * @param {any} event forward tab
   * @returns {void}
   */
  handleForwardTab(event) {
    if (document.activeElement === this.lastFocusableElement) {
      event.preventDefault();
      this.firstFocusableElement.focus();
    }
  },

  openDialog() {
    disableBodyScroll(this.$root);
    this.focusedElementBeforeOpen = document.activeElement;
    this.$root.classList.add(this.dialogIsActiveClass);
    this.$root.removeAttribute('hidden', '');
    this.firstFocusableElement.focus();
  },

  closeDialog() {
    this.$root.classList.remove(this.dialogIsActiveClass);
    this.$root.setAttribute('hidden', '');
    this.focusedElementBeforeOpen.focus();
    enableBodyScroll(this.$root);
  },

  toggleDialog() {
    if (this.isActive) {
      this.closeDialog();
    } else {
      this.openDialog();
    }
  },

  bindEvents() {
    // Close the dialog when clicking outside of it
    this.$root.addEventListener('click', (event) => {
      if (event.target === this.$root) {
        this.closeDialog();
      }
    });

    // Open dialog on button click
    const selector = `[data-js-dialog-toggle="${this.$root.id}"]`;
    delegateEvent('click', selector, document, this.openDialog.bind(this));

    // Control dialog via events
    this.$root.addEventListener('closeDialog', this.closeDialog.bind(this));
    this.$root.addEventListener('openDialog', this.openDialog.bind(this));
    this.$root.addEventListener('toggleDialog', this.toggleDialog.bind(this));

    // a11y key helpers
    this.$root.addEventListener('keydown', (event) => {
      switch (event.key) {
        // Focus never leaves the dialog and moves around
        // between focusable elements
        case 'Tab': {
          if (this.focusableElements.length === 1) {
            event.preventDefault();
            break;
          }
          if (event.shiftKey) {
            this.handleBackwardTab(event);
          } else {
            this.handleForwardTab(event);
          }
          break;
        }
        // Escape closes the top dialog (current dialog)
        case 'Escape': {
          this.closeDialog();
          break;
        }
        default: {
          break;
        }
      }
    });
  },

  init() {
    // make sure the overlay is a direct child of <body>
    if (this.$root.parentElement !== document.body) {
      document.body.appendChild(this.$root);
    }

    this.bindEvents();
  }
});

export default Dialog;
